import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useToast } from '../../providers/ToastProvider';
import { useUser } from '../../providers/UserProvider';
import {
    addPracticeUser,
    fetchPracticeUsers,
    type PracticeUser,
    removePracticeUser
} from '../../services/practiceUsers';
import AddTeamMemberModal from '../modals/AddTeamMemberModal';

interface TeamMembersProps {
    practiceId: string;
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
}

const MemberRow: React.FC<{
    member: PracticeUser;
    isSelf: boolean;
    isConfirming: boolean;
    isRemoving: boolean;
    onRemoveClick: () => void;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ member, isSelf, isConfirming, isRemoving, onRemoveClick, onConfirm, onCancel }) => (
    <div className="flex items-center justify-between py-3 group">
        <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-yellow-400 text-gray-700 text-xs font-bold shrink-0">
                {getInitials(member.fullname)}
            </div>
            <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800 truncate">
                        {member.fullname}
                    </span>
                    {isSelf && (
                        <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 uppercase tracking-wide">
                            You
                        </span>
                    )}
                </div>
                <span className="text-xs text-gray-500 truncate">{member.email}</span>
            </div>
        </div>

        {!isSelf && (
            <div className="shrink-0 ml-3">
                {isConfirming ? (
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={onConfirm}
                            disabled={isRemoving}
                            className="px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                            {isRemoving ? 'Removing...' : 'Confirm'}
                        </button>
                        <button
                            onClick={onCancel}
                            className="px-2.5 py-1 text-xs font-semibold text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={onRemoveClick}
                        className="px-2.5 py-1 text-xs font-medium text-gray-400 rounded-md sm:opacity-0 sm:group-hover:opacity-100 hover:text-red-600 hover:bg-red-50 transition-all"
                    >
                        Remove
                    </button>
                )}
            </div>
        )}
    </div>
);

const TeamMembers: React.FC<TeamMembersProps> = ({ practiceId }) => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { user } = useUser();
    const currentUserEmail = user?.email?.toLowerCase() ?? '';
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);

    const { data: members = [], isLoading } = useQuery({
        queryKey: ['practice-users', practiceId],
        queryFn: () => fetchPracticeUsers(practiceId),
        enabled: !!practiceId
    });

    const addMutation = useMutation({
        mutationFn: (input: { email: string; fullname: string }) =>
            addPracticeUser(practiceId, input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['practice-users', practiceId] });
            setIsModalOpen(false);
            showToast('Team member added successfully', 'success');
        },
        onError: (error: any) => {
            const message = error?.response?.data?.error?.message ?? 'Failed to add team member';
            showToast(message, 'error');
        }
    });

    const removeMutation = useMutation({
        mutationFn: (email: string) => removePracticeUser(practiceId, email),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['practice-users', practiceId] });
            setConfirmingRemoval(null);
            showToast('Team member removed', 'success');
        },
        onError: (error: any) => {
            const message = error?.response?.data?.error?.message ?? 'Failed to remove team member';
            showToast(message, 'error');
            setConfirmingRemoval(null);
        }
    });

    if (!practiceId) return null;

    return (
        <div className="flex flex-col gap-4">
            {/* Header — matches Practice Details sub-section header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-500">Team Members</span>
                    {!isLoading && members.length > 0 && (
                        <span className="text-[11px] font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                            {members.length}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    Add member
                </button>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex flex-col divide-y divide-gray-100">
                    {[1, 2].map((i) => (
                        <div key={i} className="flex items-center gap-3 py-3 animate-pulse">
                            <div className="w-9 h-9 bg-gray-200 rounded-full shrink-0" />
                            <div className="flex flex-col gap-1.5 flex-1">
                                <div className="h-3.5 bg-gray-200 rounded w-28" />
                                <div className="h-3 bg-gray-100 rounded w-40" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : members.length === 0 ? (
                <div className="flex flex-col items-center py-6 text-center">
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mb-2">
                        <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                    </div>
                    <p className="text-sm text-gray-500">No team members yet</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                        Add members to collaborate on this practice
                    </p>
                </div>
            ) : (
                <div className="flex flex-col divide-y divide-gray-100">
                    {members.map((member: PracticeUser) => (
                        <MemberRow
                            key={member.email}
                            member={member}
                            isSelf={member.email.toLowerCase() === currentUserEmail}
                            isConfirming={confirmingRemoval === member.email}
                            isRemoving={
                                removeMutation.isPending && confirmingRemoval === member.email
                            }
                            onRemoveClick={() => setConfirmingRemoval(member.email)}
                            onConfirm={() => removeMutation.mutate(member.email)}
                            onCancel={() => setConfirmingRemoval(null)}
                        />
                    ))}
                </div>
            )}

            <AddTeamMemberModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={(data) => addMutation.mutate(data)}
                isSubmitting={addMutation.isPending}
            />
        </div>
    );
};

export default TeamMembers;
